// Products → Vendor codes (decision 150): every code a vendor prints (Mata's first) and the product it is linked to. The
// owner and office link a code, change a wrong link, unlink it, hide one that is not a product, or make the product a
// vendor sells that is not in the catalog yet. A code's prices follow its link.
import type { Item, VendorCodeRow } from '@morales/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../auth';
import { ItemPicker } from '../components/ItemPicker';
import { Sheet, type SheetColumn } from '../components/Sheet';
import { Empty, ErrorBox, QueryView } from '../components/ui';
import { formatDate, formatMoney } from '../format';
import { useLocale } from '../i18n';
import type { AppMessageKey } from '../messages';
import { qk } from '../queryClient';
import { canEditCatalog } from './ItemsPage';

type Show = 'all' | 'unlinked' | 'linked' | 'hidden';
const SHOWS: { key: Show; label: AppMessageKey }[] = [
  { key: 'unlinked', label: 'codesUnlinked' },
  { key: 'linked', label: 'codesLinked' },
  { key: 'hidden', label: 'codesHidden' },
  { key: 'all', label: 'papersAll' },
];
const fold = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

export function VendorCodesPage() {
  const { t, intlLocale } = useLocale();
  const { me } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const editable = canEditCatalog(me?.role);
  const vendors = useQuery({ queryKey: qk.vendors, queryFn: ({ signal }) => api.vendors(signal), staleTime: 10 * 60_000 });
  // Mata's first: the vendor most papers come from
  const vendorId = params.get('vendor') ?? vendors.data?.find((v) => /mata/i.test(v.name))?.id ?? vendors.data?.[0]?.id ?? '';
  const [show, setShow] = useState<Show>('unlinked');
  const [q, setQ] = useState('');
  const [picking, setPicking] = useState<VendorCodeRow | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  const codes = useQuery({ queryKey: qk.vendorCodes(vendorId), queryFn: ({ signal }) => api.vendorCodes(vendorId, signal), enabled: !!vendorId });
  useEffect(() => {
    setPicking(null);
    setSaid(null);
  }, [vendorId]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: qk.vendorCodes(vendorId) });
    void qc.invalidateQueries({ queryKey: qk.unknownNames });
    void qc.invalidateQueries({ queryKey: ['items'] });
  };
  const change = useMutation({
    mutationFn: (v: { row: VendorCodeRow; itemId?: string | null; hidden?: boolean; itemName?: string }) =>
      api.changeVendorCode(vendorId, v.row.id, { from: v.row.itemId, ...(v.itemId !== undefined ? { itemId: v.itemId } : {}), ...(v.hidden !== undefined ? { hidden: v.hidden } : {}) }).then((r) => ({ ...r, v })),
    onSuccess: ({ code, costsAdded, v }) => {
      setPicking(null);
      setSaid(
        v.itemId && v.row.itemId
          ? t('codeMovedSaid').replace('{code}', code.vendorCode).replace('{product}', code.itemName ?? '')
          : v.itemId
          ? t('codeLinkedSaid').replace('{code}', code.vendorCode).replace('{product}', code.itemName ?? '').replace('{n}', String(costsAdded))
          : v.itemId === null
            ? t('codeUnlinkedSaid').replace('{code}', code.vendorCode)
            : (v.hidden ? t('codeHiddenSaid') : t('codeShownSaid')).replace('{code}', code.vendorCode),
      );
      refresh();
    },
    // a refusal (moved meanwhile, dock costs, a closed month) shows its reason; the list is read again
    onError: () => refresh(),
  });
  const unlink = (row: VendorCodeRow) => {
    if (window.confirm(t('codeUnlinkAsk').replace('{code}', row.vendorCode).replace('{product}', row.itemName ?? ''))) change.mutate({ row, itemId: null });
  };
  // the product search opens where the eye is
  const pickerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (picking) pickerRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [picking]);

  const counts = useMemo(() => {
    const rows = codes.data ?? [];
    return { unlinked: rows.filter((r) => !r.itemId && !r.hidden).length, linked: rows.filter((r) => r.itemId && !r.hidden).length, hidden: rows.filter((r) => r.hidden).length, all: rows.length };
  }, [codes.data]);
  const rows = useMemo(() => {
    const term = fold(q.trim());
    return (codes.data ?? [])
      .filter((r) => (show === 'all' ? true : show === 'hidden' ? r.hidden : show === 'linked' ? !!r.itemId && !r.hidden : !r.itemId && !r.hidden))
      .filter((r) => !term || fold(`${r.vendorCode} ${r.vendorDesc ?? ''} ${r.itemName ?? ''} ${r.itemSku ?? ''}`).includes(term))
      // the codes seen most lately first: the ones that matter today
      .sort((a, b) => (b.lastSeen ?? '').localeCompare(a.lastSeen ?? '') || a.vendorCode.localeCompare(b.vendorCode));
  }, [codes.data, show, q]);

  const newProduct = (r: VendorCodeRow) => navigate(`/items/new?vendor=${encodeURIComponent(vendorId)}&code=${encodeURIComponent(r.id)}&label=${encodeURIComponent(r.vendorCode)}&name=${encodeURIComponent(r.vendorDesc ?? r.vendorCode)}`);
  const busy = change.isPending;
  const columns: SheetColumn<VendorCodeRow>[] = [
    { key: 'code', header: t('vendorCodeCol'), width: '90px', render: (r) => <strong>{r.vendorCode}</strong> },
    { key: 'desc', header: t('vendorDescCol'), width: '168px', render: (r) => r.vendorDesc ?? '' },
    { key: 'product', header: t('productCol'), width: '180px', render: (r) => (r.itemId ? <Link to={`/items/${r.itemId}`}>{r.itemName}</Link> : <span className="muted">{r.hidden ? t('codesHidden') : t('codeNotLinked')}</span>) },
    { key: 'cost', header: t('costCol'), width: '72px', align: 'right', render: (r) => (r.cost ? formatMoney(r.cost.amount, intlLocale) : '') },
    { key: 'seen', header: t('lastSeenCol'), width: '108px', render: (r) => (r.lastSeen ? formatDate(r.lastSeen, intlLocale, 'date') : '') },
    { key: 'papers', header: t('papersCol'), width: '52px', align: 'right', render: (r) => String(r.papers) },
    ...(editable
      ? [
          {
            key: 'act',
            header: '',
            width: '270px',
            render: (r: VendorCodeRow) => (
              <span className="cell-actions">
                <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => setPicking(r)}>
                  {r.itemId ? t('codeChange') : t('codeLink')}
                </button>
                {r.itemId ? (
                  <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => unlink(r)}>
                    {t('codeUnlink')}
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => newProduct(r)}>
                      {t('codeNewProduct')}
                    </button>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={() => change.mutate({ row: r, hidden: !r.hidden })}>
                      {r.hidden ? t('codeShow') : t('codeHide')}
                    </button>
                  </>
                )}
              </span>
            ),
          } satisfies SheetColumn<VendorCodeRow>,
        ]
      : []),
  ];

  return (
    <div className="desk-page stack">
      <header className="desk-head">
        <div>
          <h1>{t('vendorCodesTitle')}</h1>
          <p className="muted">{t('vendorCodesHint')}</p>
        </div>
        <div className="row-actions">
          <Link to="/items/linking" className="btn btn-secondary btn-sm">
            {t('linkingTitle')}
          </Link>
          <Link to="/items" className="btn btn-secondary btn-sm">
            {t('navProducts')}
          </Link>
        </div>
      </header>
      <div className="toolbar">
        <label className="tool">
          <span className="label">{t('vendor')}</span>
          <select value={vendorId} onChange={(e) => setParams({ vendor: e.target.value }, { replace: true })} aria-label={t('vendor')}>
            {(vendors.data ?? []).map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        </label>
        <input className="tool-search" type="search" placeholder={t('codesFind')} aria-label={t('codesFind')} value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <div className="chips" role="group" aria-label={t('vendorCodesTitle')}>
        {SHOWS.map((s) => (
          <button key={s.key} type="button" className="chip" aria-pressed={show === s.key} onClick={() => setShow(s.key)}>
            {t(s.label)} <strong>{counts[s.key]}</strong>
          </button>
        ))}
      </div>
      {said ? (
        <p className="flash" role="status">
          {said}
        </p>
      ) : null}
      {change.error ? <ErrorBox error={change.error} /> : null}
      {picking ? (
        <section ref={pickerRef} className="card stack-sm" aria-label={t('codeLinkTo')}>
          <h2>
            {t('codeLinkTo')} {picking.vendorCode}
            {picking.vendorDesc ? ` · ${picking.vendorDesc}` : ''}
          </h2>
          <ItemPicker key={picking.id} initial={firstWord(picking.vendorDesc)} busy={busy} onCancel={() => setPicking(null)} onPick={(item: Item) => change.mutate({ row: picking, itemId: item.id, itemName: item.name })} />
        </section>
      ) : null}
      <QueryView query={codes}>
        {() => (rows.length === 0 ? <Empty>{t('codesNone')}</Empty> : <Sheet label={t('vendorCodesTitle')} columns={columns} rows={rows} rowKey={(r) => r.id} rowLabel={(r) => r.vendorCode} minWidth="940px" stickyFirst />)}
      </QueryView>
    </div>
  );
}

/** the first word of the vendor's description, to start the search with ("TOMATO ROMA 25#" → "tomato") */
function firstWord(desc: string | null): string {
  const w = (desc ?? '').toLowerCase().match(/[a-zñáéíóú]{3,}/)?.[0];
  return w ?? '';
}
